import { create } from "zustand";
import { useAppLoading } from "./useAppLoading";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useCurrentLocation } from "./useCurrentLocation";

interface IMaterialCompositionPlastics {
  pet?: number;
  pp?: number;
  hdpe?: number;
  ldpe?: number;
  ps?: number;
  drinkingCartoons?: number;
  misc?: number;
  nonPackagingPlastic?: number;
}

export interface IQualityInput {
  _id?: string;
  dropDate: Date;
  totalWeight: number;
  pet?: number;
  pp?: number;
  hdpe?: number;
  ldpe?: number;
  ps?: number;
  drinkingCartoons?: number;
  misc?: number;
  nonPackagingPlastic?: number;
  nonPlasticOrganic?: number;
  qualityAssessment: 1 | 2 | 3 | 4 | 5;
  // feedbackOptions?: any;
  image?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

interface ITrendingLine {
  name: string;
  data: number[];
}

interface IStore {
  plasticPollutionTrendingLine: ITrendingLine[];
  plasticQualityStats: ITrendingLine[];
  allQualityInputs: IQualityInput[];
  oneQualityInput: IQualityInput | null;
  materialCompositionPlastics: IMaterialCompositionPlastics;
  hasNextPage: boolean;
  createdQualityInput: string | null;
  updatedQualityInput: string | null;
  deletedQualityInput: string | null;
  getMaterialCompositionPlastics: (
    periodDate: Date,
    period: "daily" | "weekly" | "monthly" | "yearly"
  ) => void;
  getAllQualityInputs: (page?: number, limit?: number) => void;
  getQualityInput: (id: string) => void;
  createQualityInput: (qualityInput: IQualityInput) => void;
  updateQualityInput: (
    qualityInputId: string,
    qualityInput: IQualityInput
  ) => void;
  getPlasticPollutionTrendingLine: (
    requestedDay?: Date,
    period?: "daily" | "weekly" | "monthly" | "yearly"
  ) => void;
  getPlasticQualityStats: (
    requestedDay?: Date,
    period?: "daily" | "weekly" | "monthly" | "yearly"
  ) => void;
  deleteQualityInput: (id: string) => void;
  clearCreatedQualityInput: () => void;
  clearUpdatedQualityInput: () => void;
  clearDeletedQualityInput: () => void;
}

export const useQualityInput = create<IStore>()((set) => ({
  allQualityInputs: [],
  plasticPollutionTrendingLine: [],
  plasticQualityStats: [],
  materialCompositionPlastics: {
    pet: 0,
    pp: 0,
    hdpe: 0,
    ldpe: 0,
    ps: 0,
    drinkingCartoons: 0,
    misc: 0,
    nonPackagingPlastic: 0,
  },
  oneQualityInput: null,
  hasNextPage: false,
  createdQualityInput: null,
  updatedQualityInput: null,
  deletedQualityInput: null,
  getPlasticQualityStats: async (
    requestedDay: Date = new Date(),
    period: "daily" | "weekly" | "monthly" | "yearly" = "daily"
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const currentLocation = useCurrentLocation.getState().currentLocation;
      const response = await axios.get(
        `/quality-input/stats/plastic-quality?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
      );
      set({ plasticQualityStats: response?.data });
    } catch (error) {
      LOG.error("Error on fetching Plastic Quality Stats", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getPlasticPollutionTrendingLine: async (
    requestedDay: Date = new Date(),
    period: "daily" | "weekly" | "monthly" | "yearly" = "daily"
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const currentLocation = useCurrentLocation.getState().currentLocation;
      const response = await axios.get(
        `/quality-input/stats/plastic-pollution-trending?requestedDay=${requestedDay}&period=${period}&location=${currentLocation}`
      );
      set({ plasticPollutionTrendingLine: response?.data });
    } catch (error) {
      LOG.error("Error on fetching Plastic Pollution Trending", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getMaterialCompositionPlastics: async (
    periodDate: Date,
    period: "daily" | "weekly" | "monthly" | "yearly"
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const currentLocation = useCurrentLocation.getState().currentLocation;
      const response = await axios.get(
        `/quality-input/stats/material-composition-plastic?requestedDay=${periodDate}&period=${period}&location=${currentLocation}`
      );
      set({ materialCompositionPlastics: response?.data });
    } catch (error) {
      LOG.error("Error on fetching Material Composition Plastics", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getAllQualityInputs: async (page = 1, limit = 20) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `/quality-input?page=${page}&limit=${limit}`
      );
      set((state) => ({
        ...state,
        hasNextPage: response?.data?.length === limit,
        allQualityInputs:
          page === 1
            ? response?.data
            : [...state.allQualityInputs, ...response?.data],
      }));
    } catch (error) {
      LOG.error("Error on fetching Quality Inputs", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getQualityInput: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`/quality-input/${id}`);
      set({ oneQualityInput: response?.data });
    } catch (error) {
      LOG.error("Error on fetching Quality Input", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  createQualityInput: async (qualityInput: IQualityInput) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("/quality-input", qualityInput);
      if (response?.data) {
        const newQualityInput = response.data;
        set((state) => ({
          allQualityInputs: [newQualityInput, ...state.allQualityInputs],
          createdQualityInput: newQualityInput._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on creating Quality Input", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateQualityInput: async (qualityInputId, qualityInput: IQualityInput) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(
        `/quality-input/${qualityInputId}`,
        qualityInput
      );
      if (response?.data) {
        const updatedQualityInput = response.data;
        set((state) => ({
          allQualityInputs: state.allQualityInputs.map((qualityInput) => {
            if (qualityInput._id === updatedQualityInput._id) {
              return updatedQualityInput;
            }
            return qualityInput;
          }),
          updatedQualityInput: updatedQualityInput._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating Quality Input", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteQualityInput: async (id: string) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`/quality-input/${id}`);
      set((state) => ({
        allQualityInputs: state.allQualityInputs.filter(
          (qualityInput) => qualityInput._id !== id
        ),
      }));
    } catch (error) {
      LOG.error("Error on deleting Quality Input", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearCreatedQualityInput: () => set({ createdQualityInput: null }),
  clearUpdatedQualityInput: () => set({ updatedQualityInput: null }),
  clearDeletedQualityInput: () => set({ deletedQualityInput: null }),
}));
